/**
 * AppealChat — the single, persistent chat window that *is* the Appeal
 * Letter tab. The conversation is an **append-only stream**: every agent
 * activity feed, document request, action, user note, and version card is
 * a permanent entry that never disappears — exactly like a chat transcript.
 *
 * Actions (Generate / Upload / Synthesize) live *in the stream* too. They
 * render as a button; once clicked, the button is consumed — it disappears
 * and the entry becomes a descriptive line ("Generate letter",
 * "Synthesize medical_record"), like the user's turn in a conversation.
 * The agent's response (a new activity card, a request, a version card) is
 * appended below it. The next decision point appends a new action button
 * further down. Nothing is ever replaced.
 *
 * The only fixed control is the refinement input at the bottom, which
 * appears once a draft exists (refinement is open-ended, not one-shot).
 *
 * The complete letter preview/diff/edit is NOT here — it lives in the left
 * panel (LetterPreview in LetterView.tsx).
 */

import { useEffect, useRef, useState } from "react";
import {
  Check,
  GitCompareArrows,
  Loader2,
  Paperclip,
  Send,
  Sparkles,
  Wand2,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAppStore } from "@/store/app";
import {
  useGenerateSynthetic,
  useGetProgress,
  useGetSession,
  useRefineLetter,
  useRevisions,
  useRunAgent,
  useSelectRevision,
  useUploadDocument,
} from "@/hooks/api";
import {
  isClaimDoc,
  isDenialDoc,
  SYNTHESIZABLE_KINDS,
  UPLOADABLE_KINDS,
} from "@/lib/document-kinds";
import type {
  ChatMessage,
  DocumentKind,
  DocumentRequest,
  LetterRevisionInfo,
  ProgressEntry,
} from "@/api/types";

const SUGGESTED_CHIPS = [
  "Make tone more assertive",
  "Shorten by half",
  "Add specific CPT codes",
  "Cite the medical record more explicitly",
];

const SOURCE_LABEL: Record<LetterRevisionInfo["source"], string> = {
  initial: "Initial draft",
  refine: "Refined",
  manual: "Manual edit",
};

const SOURCE_BADGE_TONE: Record<LetterRevisionInfo["source"], string> = {
  initial: "border-sky-300 bg-sky-50 text-sky-800",
  refine: "border-violet-300 bg-violet-50 text-violet-800",
  manual: "border-amber-300 bg-amber-50 text-amber-800",
};

const uid = () => Math.random().toString(36).slice(2);

/** Open the native file picker and resolve with the chosen PDF (or null
 * if the user cancels). */
function pickPdfFile(): Promise<File | null> {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".pdf";
  return new Promise<File | null>((res) => {
    input.onchange = () => res(input.files?.[0] ?? null);
    input.oncancel = () => res(null);
    input.click();
  });
}

/** Read the session's cumulative progress log directly (used to bracket a
 * run's activity slice — the polled query stops between runs). */
async function fetchProgress(sessionId: string): Promise<ProgressEntry[]> {
  try {
    const r = await fetch(`/api/sessions/${sessionId}/progress`);
    const b = await r.json();
    return (b?.data ?? []) as ProgressEntry[];
  } catch {
    return [];
  }
}

export function AppealChat() {
  // ---- Agent lifecycle state (from POST /run) -------------------------
  const status = useAppStore((s) => s.agentStatus);
  const sessionId = useAppStore((s) => s.sessionId);
  const pendingRequest = useAppStore((s) => s.pendingRequest);
  const letterHtml = useAppStore((s) => s.letterHtml);
  const setView = useAppStore((s) => s.setView);
  const setAgentResult = useAppStore((s) => s.setAgentResult);
  const setAgentStatus = useAppStore((s) => s.setAgentStatus);
  const addPending = useAppStore((s) => s.addPending);
  const removePending = useAppStore((s) => s.removePending);
  const pendingCount = useAppStore((s) => s.pendingEntries.length);

  // ---- Chat stream (append-only, local-only history) ------------------
  const messages = useAppStore((s) => s.refinementMessages);
  const append = useAppStore((s) => s.appendRefinementMessage);
  const freezeActivity = useAppStore((s) => s.freezeActivity);
  const consumeAction = useAppStore((s) => s.consumeAction);
  const morphActionToTask = useAppStore((s) => s.morphActionToTask);
  const completeTask = useAppStore((s) => s.completeTask);
  const appendRevision = useAppStore((s) => s.appendRevision);
  const setCurrentRevision = useAppStore((s) => s.setCurrentRevision);
  const revisions = useAppStore((s) => s.revisions);
  const currentRevisionId = useAppStore((s) => s.currentRevisionId);

  const { data: session } = useGetSession(sessionId);
  const documents = session?.documents ?? [];
  const syntheticLocked = session?.synthetic_locked ?? false;
  // The agent needs *one* claim doc (837 or UB04) and *one* denial doc
  // (835 or EOB) — the paper-form equivalents carry the same info.
  const hasClaimDoc = documents.some((d) => isClaimDoc(d.kind));
  const hasDenialDoc = documents.some((d) => isDenialDoc(d.kind));
  // Any in-flight upload / synth — block "Generate" until the doc set is
  // stable so we don't run against a half-populated session.
  const synthInProgress = pendingCount > 0;

  // Live activity feed during a run. The cumulative log is sliced per
  // activity card via its ``fromIndex``.
  const { data: progress } = useGetProgress(sessionId, {
    polling: status === "running",
  });
  const { data: revisionsData, refetch: refetchRevisions } =
    useRevisions(sessionId);

  const { runAgent } = useRunAgent();
  const { generateSynthetic } = useGenerateSynthetic();
  const { uploadDocument } = useUploadDocument();
  const { refineLetter, isPending: refining } = useRefineLetter();
  const { selectRevision, isPending: switching } = useSelectRevision();

  // Synchronous guards against rapid double-clicks before React re-renders.
  const provideRef = useRef(false);
  const runRef = useRef(false);
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const requestedDocPresent =
    !!pendingRequest && documents.some((d) => d.kind === pendingRequest.kind);
  const composerActive = !!letterHtml;

  // Keep the store's revision list in sync with the server's.
  useEffect(() => {
    if (revisionsData) useAppStore.setState({ revisions: revisionsData });
  }, [revisionsData]);

  // Append a version card for any revision that isn't yet in the stream
  // (the initial draft from a run; refine adds its own card eagerly).
  useEffect(() => {
    if (!revisionsData?.length) return;
    const seen = new Set(
      messages.filter((m) => m.revisionId).map((m) => m.revisionId),
    );
    const toAdd = revisionsData.filter((r) => !seen.has(r.id));
    if (!toAdd.length) return;
    for (const rev of toAdd) {
      append({
        role: "assistant",
        content: rev.change_summary ?? SOURCE_LABEL[rev.source],
        at: rev.created_at,
        revisionId: rev.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revisionsData]);

  // Drop a "Generate letter" action button into the stream whenever one is
  // appropriate and not already present: at the start (docs ready) and to
  // resume after a requested doc lands.
  useEffect(() => {
    if (composerActive) return;
    if (status === "running" || status === "error") return;
    if (messages.some((m) => m.kind === "activity" && m.live)) return;
    const readyToStart =
      status === "idle" && hasClaimDoc && hasDenialDoc && !synthInProgress;
    const readyToResume = status === "needs_document" && requestedDocPresent;
    if (!readyToStart && !readyToResume) return;
    // Read live store state (not the render-snapshot `messages`) so the
    // guard sees a generate action appended a moment earlier — e.g. by
    // StrictMode's double-invoke of this effect — and stays idempotent.
    // zustand's set is synchronous, so the second invocation sees the
    // first append and bails instead of stacking a duplicate button.
    const hasLiveGenerate = useAppStore
      .getState()
      .refinementMessages.some(
        (m) => m.kind === "action" && m.action === "generate" && !m.consumed,
      );
    if (hasLiveGenerate) return;
    append({
      role: "assistant",
      content: "",
      at: new Date().toISOString(),
      kind: "action",
      action: "generate",
      actionId: uid(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    status,
    hasClaimDoc,
    hasDenialDoc,
    synthInProgress,
    requestedDocPresent,
    composerActive,
    messages,
  ]);

  // Auto-scroll as the stream / live activity grows.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, progress, refining]);

  // ---- Run a turn: append a live activity card, then freeze it --------
  async function startRun() {
    if (!sessionId || runRef.current) return;
    runRef.current = true;
    const before = await fetchProgress(sessionId);
    const activityId = uid();
    append({
      role: "assistant",
      content: "",
      at: new Date().toISOString(),
      kind: "activity",
      activityId,
      fromIndex: before.length,
      live: true,
      progress: [],
    });
    setAgentStatus("running");
    const prevAppealType = useAppStore.getState().appealType;
    try {
      const result = await runAgent(sessionId);
      const after = await fetchProgress(sessionId);
      freezeActivity(activityId, after.slice(before.length));
      setAgentResult({
        status: result.status,
        appealType: result.appeal_type,
        appealTypeRationale: result.appeal_type_rationale,
        pendingRequest: result.pending_request,
        analysisSummary: result.analysis_summary,
        letterHtml: result.letter_html,
        currentRevisionId: result.current_revision_id,
      });
      // Surface the inferred appeal type as its own card the first time it
      // appears or whenever it changes.
      if (result.appeal_type && result.appeal_type !== prevAppealType) {
        append({
          role: "assistant",
          content: result.appeal_type,
          at: new Date().toISOString(),
          kind: "appeal_type",
          rationale: result.appeal_type_rationale ?? undefined,
        });
      }
      if (result.status === "needs_document" && result.pending_request) {
        append({
          role: "assistant",
          content: "",
          at: new Date().toISOString(),
          kind: "request",
          request: result.pending_request,
        });
        append({
          role: "assistant",
          content: "",
          at: new Date().toISOString(),
          kind: "action",
          action: "provide",
          actionId: uid(),
        });
      } else if (result.status === "complete") {
        // Surface the v1 card (the run mutation doesn't invalidate the
        // revisions query) and flip the left panel to the finished letter.
        await refetchRevisions();
        setView("letter");
        toast.success("Appeal letter ready");
      }
    } catch (e) {
      freezeActivity(
        activityId,
        await fetchProgress(sessionId).then((p) => p.slice(before.length)),
      );
      setAgentStatus("error");
      const msg = e instanceof Error ? e.message : String(e);
      append({
        role: "assistant",
        content: `Agent run failed: ${msg}`,
        at: new Date().toISOString(),
        error: true,
      });
      // Offer a retry button in the stream.
      append({
        role: "assistant",
        content: "",
        at: new Date().toISOString(),
        kind: "action",
        action: "generate",
        actionId: uid(),
      });
      toast.error(`Agent run failed: ${msg}`);
    } finally {
      runRef.current = false;
    }
  }

  function onGenerate(item: ChatMessage) {
    if (!item.actionId) return;
    consumeAction(item.actionId, "Generate letter");
    // Optimistically flip to running so the append-generate effect won't
    // re-add a button during startRun's async setup.
    setAgentStatus("running");
    startRun();
  }

  // The mutation half of providing a doc (no file-pick, no UI — those are
  // handled by onProvide). Returns whether the doc was added.
  async function doProvide(
    via: "upload" | "synth",
    file: File | null,
    kind: DocumentKind,
  ): Promise<boolean> {
    if (!sessionId || provideRef.current) return false;
    provideRef.current = true;
    const tk = uid();
    addPending({ token: tk, kind, mode: via === "synth" ? "synthesize" : "upload" });
    try {
      if (via === "synth") {
        await generateSynthetic({ sessionId, kind });
      } else if (file) {
        await uploadDocument({ sessionId, kind, file });
      }
      return true;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg);
      return false;
    } finally {
      removePending(tk);
      provideRef.current = false;
    }
  }

  async function onProvide(item: ChatMessage, via: "upload" | "synth") {
    if (!item.actionId || !pendingRequest) return;
    const kind = pendingRequest.kind as DocumentKind;
    let file: File | null = null;
    if (via === "upload") {
      file = await pickPdfFile();
      if (!file) return; // cancelled — keep the button live
    }
    // The button morphs into a live "thinking" line for the operation.
    const verb = via === "upload" ? "Uploading" : "Synthesizing";
    morphActionToTask(item.actionId, `${verb} ${kind}…`);
    const ok = await doProvide(via, file, kind);
    const done = via === "upload" ? "Uploaded" : "Synthesized";
    completeTask(
      item.actionId,
      ok ? `${done} ${kind}` : `Couldn't add ${kind} — try again`,
      !ok,
    );
    if (!ok) {
      // Re-offer the provide buttons so the user can retry.
      append({
        role: "assistant",
        content: "",
        at: new Date().toISOString(),
        kind: "action",
        action: "provide",
        actionId: uid(),
      });
    }
  }

  async function onSend() {
    const text = input.trim();
    if (!text || !sessionId || refining) return;
    setInput("");
    append({ role: "user", content: text, at: new Date().toISOString() });
    try {
      const result = await refineLetter({ sessionId, instruction: text });
      setCurrentRevision(result.revision.id, result.letter_html);
      appendRevision(result.revision);
      append({
        role: "assistant",
        content: result.change_summary,
        at: new Date().toISOString(),
        revisionId: result.revision.id,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Refinement failed: ${msg}`);
    }
  }

  async function onPickRevision(revisionId: string) {
    if (!sessionId || switching) return;
    try {
      const detail = await selectRevision({ sessionId, revisionId });
      setCurrentRevision(detail.id, detail.html);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Switch failed: ${msg}`);
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-4 py-3 border-b shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-violet-500" />
          <p className="text-sm leading-5 font-medium">Appeal assistant</p>
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
          I analyze the denial, request any supporting documents, draft the
          letter, then refine it with you.
        </p>
      </div>

      <div
        ref={scrollRef}
        role="log"
        aria-live="polite"
        className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0"
      >
        {messages.length === 0 && (
          <div className="flex justify-start">
            <div className="max-w-[85%] bg-slate-100 text-foreground rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm leading-5">
              I'll analyze the denial and draft an appeal. Add a claim doc (837
              or UB04) and a payer response (835 or EOB) — or generate a
              synthetic set — and a{" "}
              <span className="font-medium">Generate letter</span> button will
              appear here.
            </div>
          </div>
        )}

        {messages.map((m, i) => {
          if (m.kind === "activity") {
            return (
              <ActivityCard
                key={m.activityId ?? i}
                item={m}
                liveProgress={progress ?? []}
              />
            );
          }
          if (m.kind === "appeal_type") {
            return (
              <AppealTypeCard
                key={i}
                type={m.content}
                rationale={m.rationale}
              />
            );
          }
          if (m.kind === "request" && m.request) {
            return <RequestCard key={i} req={m.request} />;
          }
          if (m.kind === "task") {
            return <TaskCard key={m.actionId ?? i} item={m} />;
          }
          if (m.kind === "action") {
            return (
              <ActionItem
                key={m.actionId ?? i}
                item={m}
                pendingRequest={pendingRequest}
                uploadDisabled={syntheticLocked}
                generateDisabled={synthInProgress}
                onGenerate={() => onGenerate(m)}
                onUpload={() => onProvide(m, "upload")}
                onSynth={() => onProvide(m, "synth")}
              />
            );
          }
          return (
            <ChatBubble
              key={i}
              message={m}
              revisions={revisions}
              currentRevisionId={currentRevisionId}
              onPickRevision={onPickRevision}
            />
          );
        })}

        {refining && (
          <div className="flex justify-start">
            <div className="bg-slate-100 text-muted-foreground rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Revising…
            </div>
          </div>
        )}
      </div>

      {composerActive && (
        <div className="p-3 border-t space-y-2 shrink-0">
          <RefineComposer
            input={input}
            setInput={setInput}
            onSend={onSend}
            refining={refining}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action item — an in-stream button that converts to a descriptive line.
// ---------------------------------------------------------------------------

function ActionItem({
  item,
  pendingRequest,
  uploadDisabled,
  generateDisabled,
  onGenerate,
  onUpload,
  onSynth,
}: {
  item: ChatMessage;
  pendingRequest: DocumentRequest | null;
  uploadDisabled: boolean;
  generateDisabled: boolean;
  onGenerate: () => void;
  onUpload: () => void;
  onSynth: () => void;
}) {
  // Once clicked, the action is consumed: render it as the user's turn.
  if (item.consumed) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-foreground text-background rounded-2xl rounded-br-sm px-3.5 py-2 text-sm leading-5">
          {item.content}
        </div>
      </div>
    );
  }

  if (item.action === "generate") {
    return (
      <div className="flex flex-col gap-1">
        <Button
          size="lg"
          className="gap-2 w-full"
          onClick={onGenerate}
          disabled={generateDisabled}
        >
          {generateDisabled ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          Generate letter
        </Button>
        {generateDisabled && (
          <p className="text-[11px] text-muted-foreground">
            Waiting for documents to finish generating…
          </p>
        )}
      </div>
    );
  }

  // provide — Upload / Synthesize the requested doc
  if (!pendingRequest) return null;
  return (
    <ProvideButtons
      kind={pendingRequest.kind}
      uploadDisabled={uploadDisabled}
      onUpload={onUpload}
      onSynth={onSynth}
    />
  );
}

function ProvideButtons({
  kind,
  uploadDisabled,
  onUpload,
  onSynth,
}: {
  kind: string;
  uploadDisabled: boolean;
  onUpload: () => void;
  onSynth: () => void;
}) {
  const synthSupported = SYNTHESIZABLE_KINDS.includes(kind as DocumentKind);
  const uploadSupported = UPLOADABLE_KINDS.includes(kind as DocumentKind);
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <p className="text-xs text-muted-foreground mb-2">
        Provide the requested <span className="font-medium">{kind}</span> to
        continue.
      </p>
      <div className="flex items-center gap-2">
        {!uploadSupported ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="flex-1">
                <Button
                  variant="outline"
                  className="gap-1.5 w-full pointer-events-none"
                  disabled
                  aria-disabled
                >
                  <Paperclip className="w-3.5 h-3.5" />
                  Upload
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              "{kind}" isn't a real-upload kind — synthesize one instead.
            </TooltipContent>
          </Tooltip>
        ) : uploadDisabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Span wrapper so the tooltip still fires while the disabled
                  button swallows pointer events. */}
              <span tabIndex={0} className="flex-1">
                <Button
                  variant="outline"
                  className="gap-1.5 w-full pointer-events-none"
                  disabled
                  aria-disabled
                >
                  <Paperclip className="w-3.5 h-3.5" />
                  Upload
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              Synthetic mode is on — real uploads are disabled. Start a new
              session to switch back.
            </TooltipContent>
          </Tooltip>
        ) : (
          <Button
            variant="outline"
            className="gap-1.5 flex-1"
            onClick={onUpload}
          >
            <Paperclip className="w-3.5 h-3.5" />
            Upload
          </Button>
        )}
        {synthSupported ? (
          <Button className="gap-1.5 flex-1" onClick={onSynth}>
            <Wand2 className="w-3.5 h-3.5" />
            Synthesize
          </Button>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="flex-1">
                <Button
                  className="gap-1.5 w-full pointer-events-none"
                  disabled
                  aria-disabled
                >
                  <Wand2 className="w-3.5 h-3.5" />
                  Synthesize
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              Synthetic generation isn't supported for "{kind}" yet — please
              upload a real file.
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bottom refinement input (only once a draft exists).
// ---------------------------------------------------------------------------

function RefineComposer({
  input,
  setInput,
  onSend,
  refining,
}: {
  input: string;
  setInput: (v: string) => void;
  onSend: () => void;
  refining: boolean;
}) {
  return (
    <>
      <div className="flex flex-wrap gap-1.5">
        {SUGGESTED_CHIPS.map((chip) => (
          <button
            key={chip}
            onClick={() => setInput(chip)}
            className="text-[11px] px-2 py-1 rounded-full bg-slate-100 hover:bg-slate-200"
          >
            {chip}
          </button>
        ))}
      </div>
      <div className="border rounded-lg focus-within:ring-2 focus-within:ring-foreground transition">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          placeholder="Type a refinement…"
          rows={2}
          className="border-0 focus-visible:ring-0"
        />
        <div className="flex items-center justify-between px-3 pb-2">
          <span className="text-[10px] text-muted-foreground">
            Enter to send · Shift+Enter newline
          </span>
          <Button
            size="sm"
            className="gap-1.5"
            onClick={onSend}
            disabled={!input.trim() || refining}
          >
            {refining ? (
              <Sparkles className="w-3 h-3 animate-pulse" />
            ) : (
              <Send className="w-3 h-3" />
            )}
            Send
          </Button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Stream items — activity feed, document request, chat bubbles, version card.
// ---------------------------------------------------------------------------

function AssistantBubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] w-full rounded-2xl rounded-bl-sm px-3.5 py-3 text-foreground border border-slate-200 bg-slate-50">
        {children}
      </div>
    </div>
  );
}

function ActivityCard({
  item,
  liveProgress,
}: {
  item: ChatMessage;
  liveProgress: ProgressEntry[];
}) {
  // Live cards slice the polled cumulative log from where this run began;
  // frozen cards render their own static snapshot.
  const entries = item.live
    ? liveProgress.slice(item.fromIndex ?? 0)
    : item.progress ?? [];
  // Activity is process, not result — render it inline (no card/border) like
  // the Claude chat window. Only result cards below keep the bordered bubble.
  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] w-full px-1 text-foreground">
        <ActivityFeed entries={entries} live={!!item.live} />
      </div>
    </div>
  );
}

function ActivityFeed({
  entries,
  live,
}: {
  entries: ProgressEntry[];
  live: boolean;
}) {
  // Cycle trailing dots so the current line feels alive between polls.
  const [dotCount, setDotCount] = useState(1);
  useEffect(() => {
    if (!live) return;
    const id = window.setInterval(() => setDotCount((n) => (n % 3) + 1), 500);
    return () => window.clearInterval(id);
  }, [live]);

  const list =
    entries.length > 0
      ? entries
      : live
        ? [{ message: "Analyzing" } as ProgressEntry]
        : [];

  return (
    <>
      <ul className="space-y-1.5 text-[12px]">
        {list.map((e, i) => {
          const isLast = i === list.length - 1;
          if (live && isLast) {
            const base = e.message.replace(/[.…\s]+$/u, "");
            return (
              <li
                key={i}
                className="flex items-start gap-2 text-foreground font-medium"
              >
                <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-500 mt-0.5 shrink-0" />
                <span className="leading-snug">
                  {base}
                  <span aria-hidden className="inline-block w-[1.5em] text-left">
                    {".".repeat(dotCount)}
                  </span>
                </span>
              </li>
            );
          }
          return (
            <li key={i} className="flex items-start gap-2 text-muted-foreground">
              <span className="text-emerald-600 mt-0.5 shrink-0">●</span>
              <span className="leading-snug">{e.message}</span>
            </li>
          );
        })}
      </ul>
      {live && (
        <p className="text-[11px] text-muted-foreground mt-3">
          This usually takes 30–60 seconds.
        </p>
      )}
    </>
  );
}

function AppealTypeCard({
  type,
  rationale,
}: {
  type: string;
  rationale?: string;
}) {
  return (
    <AssistantBubble>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
        Inferred appeal type
      </div>
      <div className="text-[10px] text-muted-foreground mb-1.5">
        Based on the documents so far — may refine as more arrive.
      </div>
      <Badge variant="emerald" className="mb-1">
        {type}
      </Badge>
      {rationale && (
        <p className="text-xs leading-5 text-muted-foreground mt-1">{rationale}</p>
      )}
    </AssistantBubble>
  );
}

function RequestCard({ req }: { req: DocumentRequest }) {
  const fields = req.fields_of_interest ?? [];
  return (
    <AssistantBubble>
      <div className="text-sm font-semibold mb-2">📎 I need one more document</div>
      <div className="text-xs grid grid-cols-[60px_1fr] gap-y-1">
        <div className="text-muted-foreground">Type</div>
        <div className="font-medium">{req.kind}</div>
        <div className="text-muted-foreground">Reason</div>
        <div className="leading-5">{req.reason}</div>
      </div>
      {fields.length > 0 && (
        <ul className="mt-2 text-xs space-y-0.5">
          {fields.map((f) => (
            <li key={f} className="text-muted-foreground">
              • {f}
            </li>
          ))}
        </ul>
      )}
    </AssistantBubble>
  );
}

function TaskCard({ item }: { item: ChatMessage }) {
  // Cycle trailing dots while live, mirroring the activity "thinking" feel.
  const [dotCount, setDotCount] = useState(1);
  useEffect(() => {
    if (!item.live) return;
    const id = window.setInterval(() => setDotCount((n) => (n % 3) + 1), 500);
    return () => window.clearInterval(id);
  }, [item.live]);

  if (item.live) {
    const base = (item.content ?? "").replace(/[.…\s]+$/u, "");
    return (
      <div className="flex justify-start">
        <div className="bg-slate-100 text-foreground rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-500 shrink-0" />
          <span>
            {base}
            <span aria-hidden className="inline-block w-[1.5em] text-left">
              {".".repeat(dotCount)}
            </span>
          </span>
        </div>
      </div>
    );
  }

  if (item.error) {
    return (
      <div className="flex justify-start">
        <div className="max-w-[90%] rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm leading-5 border border-destructive/30 bg-destructive/5 text-destructive">
          {item.content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start">
      <div className="bg-slate-100 text-muted-foreground rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm flex items-center gap-2">
        <span className="text-emerald-600 shrink-0">●</span>
        {item.content}
      </div>
    </div>
  );
}

function ChatBubble({
  message,
  revisions,
  currentRevisionId,
  onPickRevision,
}: {
  message: ChatMessage;
  revisions: LetterRevisionInfo[];
  currentRevisionId: string | null;
  onPickRevision: (revId: string) => void;
}) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-foreground text-background rounded-2xl rounded-br-sm px-3.5 py-2 text-sm leading-5">
          {message.content}
        </div>
      </div>
    );
  }

  if (message.revisionId) {
    const rev = revisions.find((r) => r.id === message.revisionId);
    const idx = revisions.findIndex((r) => r.id === message.revisionId);
    if (!rev) return null;
    return (
      <RevisionCard
        rev={rev}
        index={idx}
        active={rev.id === currentRevisionId}
        onPick={() => onPickRevision(rev.id)}
      />
    );
  }

  if (message.error) {
    return (
      <div className="flex justify-start">
        <div className="max-w-[90%] rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm leading-5 border border-destructive/30 bg-destructive/5 text-destructive">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] bg-slate-100 text-foreground rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm leading-5">
        {message.content}
      </div>
    </div>
  );
}

function RevisionCard({
  rev,
  index,
  active,
  onPick,
}: {
  rev: LetterRevisionInfo;
  index: number;
  active: boolean;
  onPick: () => Promise<void> | void;
}) {
  const setDiffingRevision = useAppStore((s) => s.setDiffingRevision);

  async function onShowChanges(e: React.MouseEvent) {
    e.stopPropagation();
    // Move head to this revision first, so toggling the diff off returns to
    // *this* letter — not whichever revision was head before.
    if (!active) await onPick();
    setDiffingRevision(rev.id);
  }

  return (
    <div className="flex justify-start">
      <button
        onClick={onPick}
        className={
          "max-w-[90%] w-full text-left rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm leading-5 border transition group " +
          (active
            ? "bg-emerald-50 border-emerald-300 ring-1 ring-emerald-200"
            : "bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50")
        }
      >
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono text-[11px] font-semibold text-slate-700">
            v{index + 1}
          </span>
          <span
            className={
              "inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded border " +
              SOURCE_BADGE_TONE[rev.source]
            }
          >
            {SOURCE_LABEL[rev.source]}
          </span>
          {active && (
            <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700 font-medium">
              <Check className="w-3 h-3" /> Current
            </span>
          )}
          <span className="text-[10px] text-muted-foreground ml-auto">
            {formatRelative(rev.created_at)}
          </span>
        </div>
        {rev.change_summary && (
          <div className="text-[13px] text-slate-700 leading-snug">
            {rev.change_summary}
          </div>
        )}
        {rev.parent_revision_id && (
          <div className="flex justify-end mt-1.5 -mb-0.5">
            <button
              onClick={onShowChanges}
              className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-1 text-[10px] text-violet-700 hover:text-violet-900 hover:underline transition"
              title="Show changes from the previous revision"
            >
              <GitCompareArrows className="w-3 h-3" />
              Show changes
            </button>
          </div>
        )}
      </button>
    </div>
  );
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString();
}
